import React, { useEffect, useState } from 'react';
import * as Icons from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { integrationsApi } from '../api/endpoints';
import { toast } from 'sonner';

const TEMPLATES = [
  { key: 'lead_welcome', label: 'Lead Welcome' },
  { key: 'payment_reminder', label: 'Payment Reminder' },
  { key: 'manual_message', label: 'Manual Message' },
  { key: 'brochure_dispatch', label: 'Brochure Dispatch' },
];

const TestSendDialog = ({ trigger, onResult }) => {
  const [open, setOpen] = useState(false);
  const [phone, setPhone] = useState('+91');
  const [name, setName] = useState('Test User');
  const [tpl, setTpl] = useState('lead_welcome');
  const [msg, setMsg] = useState('Hello from Kiana Developers CRM. This is an AiSensy connectivity test.');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    setBusy(true); setResult(null);
    try {
      const r = await integrationsApi.whatsappTestSend({ phone, name, template: tpl, message: msg });
      setResult(r);
      if (r?.mocked) {
        toast.warning(`Fallback (mock) — reason: ${r.fallback_reason || 'unknown'}`);
      } else {
        toast.success('Sent via AiSensy ✓');
      }
      onResult && onResult(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Test send failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg" data-testid="aisensy-test-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">AiSensy Connectivity Test</DialogTitle>
          <DialogDescription>Fires a real (or mocked) send and shows the raw receipt.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 mt-2">
          <div><Label>Phone (with country code)</Label><Input data-testid="aisensy-test-phone" value={phone} onChange={e => setPhone(e.target.value)} /></div>
          <div><Label>Name</Label><Input value={name} onChange={e => setName(e.target.value)} /></div>
          <div>
            <Label>Template</Label>
            <Select value={tpl} onValueChange={setTpl}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TEMPLATES.map(t => <SelectItem key={t.key} value={t.key}>{t.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Message / first template param</Label><Input value={msg} onChange={e => setMsg(e.target.value)} /></div>
          {result && (
            <pre className="text-[10px] bg-slate-50 border rounded p-2 max-h-40 overflow-auto">{JSON.stringify(result, null, 2)}</pre>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Close</Button>
          <Button data-testid="aisensy-test-send-btn" onClick={submit} disabled={busy} className="bg-brand-navy hover:bg-[#13294A]">
            {busy ? 'Sending...' : 'Send Test'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const IntegrationStatusCard = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchStatus = async () => {
    setLoading(true);
    try { setStatus(await integrationsApi.whatsappStatus()); }
    catch (e) { console.error('WhatsApp status fetch failed', e); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchStatus(); }, []);

  const configured = status?.configured;
  const campaigns = status?.campaigns || {};
  const liveCount = Object.values(campaigns).filter(Boolean).length;
  const totalCount = Object.keys(campaigns).length;

  return (
    <Card className="border-slate-200" data-testid="integration-status-card">
      <CardContent className="p-4 sm:p-5">
        <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Icons.MessageCircle className="w-5 h-5 text-emerald-600" />
              <h3 className="font-display text-lg font-semibold text-slate-800">3rd-party Integrations</h3>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">Live status of AiSensy WhatsApp + Cloud Telephony</p>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="ghost" onClick={fetchStatus} title="Refresh">
              <Icons.RotateCw className="w-4 h-4" />
            </Button>
            <TestSendDialog
              onResult={fetchStatus}
              trigger={
                <Button size="sm" variant="outline" data-testid="aisensy-open-test-btn">
                  <Icons.Send className="w-4 h-4 mr-1.5" />Test Send
                </Button>
              }
            />
          </div>
        </div>

        {loading && <div className="text-sm text-slate-400">Checking...</div>}
        {!loading && status && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* AiSensy WhatsApp */}
            <div className="border rounded-lg p-3" data-testid="aisensy-status">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icons.MessageCircle className="w-4 h-4 text-emerald-600" />
                  <span className="font-medium text-sm">AiSensy WhatsApp</span>
                </div>
                <Badge variant="outline" className={configured ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-amber-50 text-amber-700 border-amber-200'}>
                  {configured ? 'API Key OK' : 'Not configured'}
                </Badge>
              </div>
              <div className="text-[11px] text-slate-500 mb-2">{status.base_url}</div>
              <div className="text-xs font-medium text-slate-600 mb-1">Campaigns ({liveCount} / {totalCount} mapped):</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(campaigns).map(([k, v]) => (
                  <span key={k} className={`text-[10px] px-1.5 py-0.5 rounded border ${v ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-500 border-slate-200'}`}>
                    {k}
                  </span>
                ))}
              </div>
              {configured && (
                <div className="text-[10px] text-slate-500 mt-2 leading-tight">
                  ✅ API key loaded. Real sends will work <b>only</b> after you create matching API Campaigns in your AiSensy dashboard (Manage → Templates → New, then Campaigns → Launch API Campaign with names <code>lead_welcome</code>, <code>payment_reminder</code>, <code>manual_message</code>, <code>brochure_dispatch</code>). Until then, the system mock-falls-back so nothing breaks.
                </div>
              )}
            </div>

            {/* Telephony */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icons.PhoneCall className="w-4 h-4 text-violet-600" />
                  <span className="font-medium text-sm">Cloud Telephony</span>
                </div>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Mocked</Badge>
              </div>
              <div className="text-[11px] text-slate-500 mb-2">Twilio / Exotel placeholder</div>
              <div className="text-[10px] text-slate-500 leading-tight">
                Click-to-call buttons currently just trigger a <code>tel:</code> link. Provide a Twilio/Exotel SID + token to enable backend-managed dialing &amp; recording.
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default IntegrationStatusCard;
